import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../api/useProjects";
import type { Setup } from "../../../../api/useSetups";
import useSuspectedFailuresInExplanation from "../useSuspectedFailuresInExplanation";
import getQuantityFailureImpactsSeries from "./getQuantityFailureImpactsSeries";
import getQuantityGapsSerie from "./getQuantityGapsSerie";
import getQuantityUnexplainedGapsSerie from "./getQuantityUnexplainedGapsSerie";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import getQuantityManualFailureImpactsSeries from "./getQuantityManualFailureImpactsSeries";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { type Quantity } from "../../../../api/useQuantities";

const useSeries = (
  project: Project,
  analysis: ActiveAnalysis,
  indexedFailureMetadata: IndexedFailureMetadata,
  indexedFailureValues: IndexedFailureValues,
  indexedManualFailureValues: IndexedManualFailureValues,
  setup: Setup | null,
  observables: Quantity[],
  normalized: boolean,
): Highcharts.SeriesColumnOptions[] => {
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const [suspectedFailuresIncluded] = useSuspectedFailuresInExplanation({
    scope: project.id,
  });
  const quantityFailureSeries = React.useMemo(
    () =>
      getQuantityFailureImpactsSeries(
        analysis.date,
        project,
        observables,
        indexedFailureValues,
        indexedFailureMetadata,
        normalized,
        setup,
        analysis.quantityValues,
      ),
    [
      analysis.date,
      analysis.quantityValues,
      project,
      observables,
      indexedFailureValues,
      indexedFailureMetadata,
      normalized,
      setup,
    ],
  );

  const quantityManualFailureImpactsSeries = React.useMemo(
    () =>
      getQuantityManualFailureImpactsSeries(
        project,
        observables,
        analysis,
        indexedManualFailureValues,
        quantityFailureSeries.length,
      ),
    [analysis, indexedManualFailureValues, project, observables, quantityFailureSeries.length],
  );

  return React.useMemo(() => {
    const impacts = [...quantityFailureSeries, ...quantityManualFailureImpactsSeries];

    /**@note : If you're concerned about performance, you can directly associate an index of 1 and 2 for “getQuantityGapsSerie” and getQuantityUnexplainedGapsSerie, as the failure impacts are found on index 0 in the “stack” configuration. */
    const gaps = [
      getQuantityGapsSerie(observables, analysis, normalized, setup, metrologyOverrides),
      getQuantityUnexplainedGapsSerie(
        analysis,
        indexedFailureValues,
        indexedFailureMetadata,
        indexedManualFailureValues,
        observables,
        suspectedFailuresIncluded,
        normalized,
        setup,
        metrologyOverrides,
      ),
    ].map(
      serie =>
        ({
          ...serie,
          index: (serie.index ?? 0) + impacts.length,
        }) as Highcharts.SeriesColumnOptions,
    );

    return [...impacts, ...gaps];
  }, [
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    metrologyOverrides,
    normalized,
    observables,
    quantityFailureSeries,
    quantityManualFailureImpactsSeries,
    setup,
    suspectedFailuresIncluded,
  ]);
};

export default useSeries;
